import { useState, type FormEvent } from 'react';
import { UiThemeInput, type UiTheme, type UiThemePalette } from '@reform-lite/contracts';
import { useHasPermission } from '@/auth/auth-store';
import { useArchiveUiTheme, useSaveUiTheme, useUiThemes } from '@/hooks/use-ui-themes';
import { useT } from '@/i18n/use-t';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { extractApiError } from '@/lib/api-error';

const empty: UiThemeInput = {
  name: '',
  light: { brand: '#0E2D4B', accent: '#E03A50', surface: '#F5F0E8' },
  dark: { brand: '#E8EEF3', accent: '#E03A50', surface: '#0A2138' },
};

export function UiThemesPage() {
  const t = useT();
  const read = useHasPermission('ui-themes.read');
  const write = useHasPermission('ui-themes.write');
  const themes = useUiThemes(read);
  const save = useSaveUiTheme();
  const archive = useArchiveUiTheme();
  const [selected, setSelected] = useState<UiTheme | null>(null);
  const [draft, setDraft] = useState<UiThemeInput>(empty);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pending = save.isPending || archive.isPending;
  const editable = write && selected?.kind !== 'preset';
  const select = (theme: UiTheme | null) => {
    setSelected(theme);
    setDraft(
      theme ? { name: theme.name, light: { ...theme.light }, dark: { ...theme.dark } } : empty,
    );
    setEditing(true);
    setError(null);
  };
  const done = () => {
    setEditing(false);
    setSelected(null);
    setError(null);
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    const input = UiThemeInput.safeParse(draft);
    if (!input.success) {
      setError(t('themes.invalid', 'Enter a name and six-digit hex colours.'));
      return;
    }
    save.mutate(
      { id: selected?.id, input: input.data },
      { onSuccess: done, onError: (cause) => setError(extractApiError(cause)) },
    );
  };
  const confirmArchive = () => {
    if (!selected) return;
    setError(null);
    archive.mutate(selected.id, {
      onSuccess: done,
      onError: (cause) => setError(extractApiError(cause)),
    });
  };
  if (!read) return <p>{t('themes.noAccess', 'You do not have permission to view UI themes.')}</p>;
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">{t('themes.title', 'UI themes')}</h1>
        {write && (
          <Button disabled={pending} onClick={() => select(null)}>
            {t('themes.new', 'New custom theme')}
          </Button>
        )}
      </div>
      <p className="max-w-3xl text-sm text-muted-foreground">
        {t(
          'themes.scope',
          'Palettes for the standard customer UI. Assign available and default themes in Parameters.',
        )}
      </p>
      <p className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
        {t(
          'themes.placeholder',
          'The five presets currently use placeholder colours. Final colours await design approval.',
        )}
      </p>
      {themes.isLoading && <p>{t('themes.loading', 'Loading themes…')}</p>}
      {themes.error && (
        <p role="alert" className="text-destructive">
          {extractApiError(themes.error)}
        </p>
      )}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {(themes.data ?? []).map((theme) => (
          <button
            key={theme.id}
            disabled={pending}
            onClick={() => select(theme)}
            className="rounded-lg border bg-card p-4 text-left hover:border-primary"
            aria-label={theme.name}
          >
            <span className="block font-medium">{theme.name}</span>
            <span className="text-xs text-muted-foreground">
              {theme.kind === 'preset'
                ? t('themes.preset', 'Preset · read-only')
                : t('themes.custom', 'Custom')}
            </span>
            <div className="mt-3 grid grid-cols-2 gap-3">
              {(['light', 'dark'] as const).map((mode) => (
                <div key={mode}>
                  <span className="text-xs">
                    {t(`themes.${mode}`, mode === 'light' ? 'Light' : 'Dark')}
                  </span>
                  <Swatch palette={theme[mode]} />
                </div>
              ))}
            </div>
          </button>
        ))}
      </div>
      {editing && (
        <form onSubmit={submit} className="space-y-4 rounded-lg border bg-card p-5">
          <h2 className="font-semibold">
            {selected ? selected.name : t('themes.new', 'New custom theme')}
          </h2>
          <label className="block max-w-lg space-y-1">
            <span>{t('themes.name', 'Name')}</span>
            <Input
              value={draft.name}
              maxLength={120}
              required
              disabled={!editable || pending}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
          </label>
          <div className="grid gap-6 lg:grid-cols-2">
            {(['light', 'dark'] as const).map((mode) => (
              <fieldset key={mode} disabled={!editable || pending} className="space-y-3">
                <legend className="font-medium">
                  {t(`themes.${mode}`, mode === 'light' ? 'Light' : 'Dark')}
                </legend>
                {(['brand', 'accent', 'surface'] as const).map((key) => (
                  <label key={key} className="flex flex-wrap items-center gap-3">
                    <span className="w-20">
                      {t(`themes.${key}`, key.charAt(0).toUpperCase() + key.slice(1))}
                    </span>
                    <input
                      type="color"
                      aria-label={`${t(`themes.${mode}`, mode)} ${t(`themes.${key}`, key)} ${t('themes.colourPicker', 'colour picker')}`}
                      value={
                        /^#[0-9a-f]{6}$/i.test(draft[mode][key]) ? draft[mode][key] : '#000000'
                      }
                      onChange={(e) =>
                        setDraft({ ...draft, [mode]: { ...draft[mode], [key]: e.target.value } })
                      }
                    />
                    <Input
                      className="w-32 font-mono"
                      aria-label={`${t(`themes.${mode}`, mode)} ${t(`themes.${key}`, key)} HEX`}
                      maxLength={7}
                      pattern="#[0-9a-fA-F]{6}"
                      value={draft[mode][key]}
                      onChange={(e) =>
                        setDraft({ ...draft, [mode]: { ...draft[mode], [key]: e.target.value } })
                      }
                    />
                  </label>
                ))}
                <Swatch palette={draft[mode]} />
              </fieldset>
            ))}
          </div>
          {error && (
            <p role="alert" className="text-destructive">
              {error}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {editable && (
              <Button type="submit" disabled={pending}>
                {t('themes.save', 'Save theme')}
              </Button>
            )}
            <Button type="button" variant="outline" disabled={pending} onClick={done}>
              {t('themes.close', 'Close')}
            </Button>
            {editable && selected && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button type="button" variant="outline" disabled={pending}>
                    {t('themes.archive', 'Archive')}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {t('themes.archiveTitle', 'Archive this theme?')}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {t(
                        'themes.archiveHelp',
                        'This theme will be removed from terminals that received it. Change any default assignments first.',
                      )}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
                    <AlertDialogAction disabled={pending} onClick={confirmArchive}>
                      {t('themes.confirmArchive', 'Confirm archive')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </form>
      )}
    </div>
  );
}

function Swatch({ palette }: { palette: UiThemePalette }) {
  return (
    <div className="mt-1 flex h-10 overflow-hidden rounded border" aria-hidden="true">
      {Object.entries(palette).map(([key, colour]) => (
        <span
          key={key}
          className="flex-1"
          style={{ backgroundColor: /^#[0-9a-f]{6}$/i.test(colour) ? colour : 'transparent' }}
        />
      ))}
    </div>
  );
}
